if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        // initialize _lastFeaturedMediaId from the currently active media
        const activeMedia = this.querySelector('media-gallery [data-media-id].is-active');
        if (activeMedia) this._lastFeaturedMediaId = activeMedia.dataset.mediaId;

        // Initial color group filter
        const initialVariantEl = this.querySelector('variant-selects [data-selected-variant]');
        if (initialVariantEl) {
          try { this.scheduleGalleryFilter(JSON.parse(initialVariantEl.innerHTML)); } catch (e) {}
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);
          this.scheduleGalleryFilter(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        // only switch if the color changed (featured media ID changed), not just size
        const newFeaturedMediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;
        if (this._lastFeaturedMediaId !== newFeaturedMediaId) {
          this._lastFeaturedMediaId = newFeaturedMediaId;
          this.querySelector(`media-gallery`)?.setActiveMedia?.(newFeaturedMediaId, true);
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      getSelectedColor(variant) {
        const optionNames = Array.isArray(variant?.options) ? variant.options : [];
        const colorInput = this.querySelector('variant-selects input[name*="Color"]:checked, variant-selects input[name*="color"]:checked');
        const colorSelect = this.querySelector('variant-selects select[name*="Color"], variant-selects select[name*="color"]');

        return colorInput?.value || colorSelect?.value || optionNames.find((value) => {
          const normalized = String(value || '').toLowerCase().trim();
          return [
            'white', 'black', 'red', 'brown', 'green', 'pink', 'orange', 'purple', 'yellow', 'blue',
            'gray', 'grey', 'modena', 'lightyear white', 'wormhole black', 'atomic red'
          ].includes(normalized);
        });
      }

      normalizeColor(value) {
        const normalized = String(value || '').toLowerCase().trim();
        if (['white', 'lightyear white'].includes(normalized)) return 'white';
        if (['black', 'wormhole black'].includes(normalized)) return 'black';
        if (['red', 'atomic red'].includes(normalized)) return 'red';
        if (['modena'].includes(normalized)) return 'purple';
        return normalized;
      }

      mediaMatchesColor(mediaAlt, color) {
        const alt = String(mediaAlt || '').toLowerCase();
        if (!alt || !color) return false;

        const colorAliases = {
          white: ['white', 'wh', 'lightyear white', '白', '白豚白'],
          black: ['black', 'bk', 'wormhole black', '黑', '白豚黑'],
          red: ['red', 'rd', 'atomic red', '红', '白豚红'],
          brown: ['brown', 'br', 'ch', 'choco brown'],
          green: ['green', 'gr', 'aurora green'],
          pink: ['pink', 'pk', 'hot pink', 'misty pink'],
          orange: ['orange', 'or'],
          purple: ['purple', 'pp', 'cosmic purple'],
          yellow: ['yellow', 'yl', 'equator yellow'],
          blue: ['blue', 'bl', 'lightsaber blue'],
          gray: ['gray', 'grey', 'gy', 'meteorite gray'],
          grey: ['gray', 'grey', 'gy', 'meteorite gray'],
        };
        const aliases = colorAliases[color] || [color];
        const tokens = alt.split(/[^a-z0-9]+/).filter(Boolean);

        return aliases.some((alias) => {
          const normalizedAlias = String(alias).toLowerCase().trim();
          if (!normalizedAlias) return false;
          if (normalizedAlias.length <= 2) return tokens.includes(normalizedAlias);
          return alt.includes(normalizedAlias);
        });
      }

      forceLoadActiveMedia(activeItem) {
        if (!activeItem) return;

        activeItem.hidden = false;
        activeItem.style.display = '';
        activeItem.classList.add('is-active');

        const image = activeItem.querySelector('img');
        if (!image) return;

        image.loading = 'eager';
        image.decoding = 'async';
        try { image.fetchPriority = 'high'; } catch (e) {}

        if (image.dataset?.src && !image.getAttribute('src')) {
          image.setAttribute('src', image.dataset.src);
        }

        if (typeof image.decode === 'function' && (!image.complete || image.naturalWidth === 0)) {
          image.decode().catch(() => {});
        }
      }

      activateFilteredMedia(gallery, activeItem, shouldPrepend = true) {
        if (!gallery || !activeItem) return;

        this.forceLoadActiveMedia(activeItem);

        if (typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(activeItem.dataset.mediaId, shouldPrepend);
          this.forceLoadActiveMedia(activeItem);
          return;
        }

        const thumb = gallery.querySelector('[data-target="' + activeItem.dataset.mediaId + '"]');
        if (thumb) {
          thumb.hidden = false;
          thumb.style.display = '';
          thumb.querySelector('button.thumbnail')?.setAttribute('aria-current', true);
        }

        customElements.whenDefined('media-gallery').then(() => {
          gallery.setActiveMedia?.(activeItem.dataset.mediaId, shouldPrepend);
          this.forceLoadActiveMedia(activeItem);
        }).catch(() => {});
      }

      scheduleGalleryFilter(variant) {
        if (!variant) return;
        this.filterGalleryByColor(variant);
        requestAnimationFrame(() => this.filterGalleryByColor(variant));
        setTimeout(() => this.filterGalleryByColor(variant), 300);
      }

      // Show only images belonging to the selected color group.
      // Images should include a color name or color code in their alt text/file name.
      filterGalleryByColor(variant) {
        const gallery = this.querySelector('media-gallery');
        if (!gallery) return;

        gallery.setAttribute('data-color-ready', '');

        const selectedColor = this.normalizeColor(this.getSelectedColor(variant));
        if (!selectedColor) return;

        const mainItems = Array.from(gallery.querySelectorAll('ul.product__media-list > li[data-media-id]'));
        const thumbItems = Array.from(gallery.querySelectorAll('.thumbnail-list__item'));
        const visibleItems = mainItems.filter((item) => this.mediaMatchesColor(item.dataset.mediaAlt, selectedColor));

        if (!visibleItems.length) {
          mainItems.forEach((item) => {
            item.hidden = false;
            item.style.display = '';
          });
          thumbItems.forEach((item) => {
            item.hidden = false;
            item.style.display = '';
          });
          return;
        }

        mainItems.forEach((item) => {
          const matches = visibleItems.includes(item);
          item.hidden = !matches;
          item.style.display = matches ? '' : 'none';
          item.classList.toggle('is-active', false);
        });

        thumbItems.forEach((item) => {
          const matches = this.mediaMatchesColor(item.dataset.mediaAlt, selectedColor);
          item.hidden = !matches;
          item.style.display = matches ? '' : 'none';
          item.querySelector('button.thumbnail')?.removeAttribute('aria-current');
        });

        const featuredMediaId = variant?.featured_media?.id ? `${this.dataset.section}-${variant.featured_media.id}` : null;
        const preferred = featuredMediaId ? visibleItems.find((item) => item.dataset.mediaId === featuredMediaId) : null;
        const activeItem = preferred || visibleItems[0];
        if (activeItem) {
          this.activateFilteredMedia(gallery, activeItem, true);
          this._lastFeaturedMediaId = activeItem.dataset.mediaId;
        }
      }
    }
  );
}

(() => {
  const replacements = {
    'Ultra-Light': 'Thin Sole Feel',
    'Featherlight design reduces weight for effortless everyday wear.': 'A low-profile sole keeps the foot close to the ground for a lighter, more natural feel.',
    'All-Day Comfort': 'Barely-There Weight',
    'Soft cushioning designed for relaxed daily movement.': 'An ultra-light 3D-printed build reduces bulk for easy everyday wear.',
    'Breathable Structure': 'Open-Air Structure',
    'Open design allows air to circulate freely around the foot.': 'The lattice upper keeps airflow moving around the foot.',
    'Lattice Structure': 'Sculpted Lattice Support',
    'A sculpted lattice design balancing airflow, stability, and modern aesthetics.': 'A structured 3D lattice balances airflow with a more supported feel.',
    'Stable Support': 'Stable Everyday Base',
    'Engineered geometry provides natural structure and everyday support.': 'Built to feel grounded for daily walking and casual movement.',
    'Modern Design': 'Modern Statement Shape',
    'A bold silhouette made to pair easily with contemporary style.': 'A bolder silhouette made to pair with clean, contemporary style.',
    'Breathable 3D lattice': 'Cushioned Recovery Feel',
    'Open structure helps air move around your feet during slow, relaxed moments.': 'A thicker 3D-printed sole gives Dolphin a softer, more relaxed underfoot feel.',
    'Soft recovery feel': 'Breathable Comfort',
    'Lightweight polymer cushioning made for home, travel reset, and post-workout downtime.': 'Open lattice construction helps keep the shoe airy during home, travel, and downtime.',
    'Easy slip-on shape': 'Easy Slip-On Shape',
    'A simple lounge fit for reading, errands, and everyday indoor-outdoor comfort.': 'Made for relaxed daily movement, quick errands, and post-activity recovery hours.',
    'Pure Comfort': 'Barely-There Feel'
  };

  const applyStarayProductCopy = () => {
    document.querySelectorAll('.product__description, .product__title, .cloud-hdr').forEach((root) => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);
      textNodes.forEach((node) => {
        let value = node.nodeValue;
        Object.entries(replacements).forEach(([from, to]) => {
          value = value.split(from).join(to);
        });
        node.nodeValue = value;
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyStarayProductCopy);
  } else {
    applyStarayProductCopy();
  }
  window.addEventListener('pageshow', applyStarayProductCopy);
  setTimeout(applyStarayProductCopy, 250);
  setTimeout(applyStarayProductCopy, 1000);
})();
